import React, { useState } from "react";
import {
  Container, HeaderRow, Title, HeaderMeta, Badge, DividerDot, Small, PageContent, Grid, Card, CardHeader, CardTitle,
  Note, Mono, List, Item, Key, Desc, EndpointRow, EndpointLabel, EndpointValue, CodeBlock, CodeArea, Toolbar, LangPill,
  Spacer, CopyBtn, Tabs, Tab, ErrorList, ErrorRow, ErrCode, ErrText, WorkflowSteps, Step, StepNumber, StepContent, StepTitle,
  StepDesc, FlowDiagram, FlowStep, FlowLabel, FlowArrow, SubTitle, StatusPill, RequiredBadge, OptionalBadge, Callout,
} from "../../Components/Documentation/DocumentationStyles";
import { Copy } from "lucide-react";
import useCopyWithFeedback from "../../hooks/useCopyWithFeedback";

const MeetingsApiDocumentation = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL || "";
  const [activeTab, setActiveTab] = useState("flow");
  const { copied, handleCopy } = useCopyWithFeedback();

  const endpoint = `${API_BASE_URL.replace(/\/?$/, "/")}api/v1/connector/meetings_api/meetings`;

  const meetingResponse = `{
  "meeting": {
    "id": 14231,
    "status": "scheduled",
    "title": "Renewal call",
    "description": "Quarterly renewal",
    "start_at": "2026-11-03T14:00:00.000Z",
    "end_at": "2026-11-03T15:00:00.000Z",
    "started_at": null,
    "ended_at": null,
    "ended_reason": null,
    "join_url": "https://…",
    "display_id": "482913057",
    "passcode": "731942",
    "organizers": [{ "email": "rep@acme.com" }],
    "invitees": [{ "email": "client@example.com" }],
    "ai_summary_enabled": true,
    "transcript_enabled": true,
    "external_reference": "crm-opportunity-8812",
    "created_at": "2026-10-13T09:12:44.000Z",
    "updated_at": "2026-10-13T09:12:44.000Z"
  }
}`;

  const listResponse = `{
  "meetings": [ { "id": 14231, "status": "scheduled", "title": "Renewal call", … } ],
  "pagination": {
    "current_page": 1,
    "total_pages": 3,
    "total_count": 61
  }
}`;

  const summaryResponse = `{
  "status": "completed",
  "summaries": [
    {
      "language": "en-US",
      "parts": [{ "part_number": 1, "content": "Summary of the renewal call…" }]
    },
    {
      "language": "ro-RO",
      "parts": [{ "part_number": 1, "content": "Rezumatul apelului…" }]
    }
  ]
}`;

  const transcriptResponse = `{
  "entries": [
    {
      "speaker": "Alex Rep",
      "language": "en-US",
      "spoken_at": "2026-11-03T14:02:11Z",
      "text": "Hello, thanks for joining.",
      "translations": { "ro-RO": "Salut, mulțumesc că v-ați alăturat." }
    }
  ],
  "text": "[00:00:00] Alex Rep: Hello, thanks for joining.\\n…"
}`;

  const webhookPayload = `POST https://crm.example.com/emotii/webhooks
Content-Type: application/json
Emotii-Event-Id: 7f1c2a9e-4b1d-4e0a-9a51-2f7c8d1e6b30
Emotii-Event-Type: meeting.ended
Emotii-Signature: t=1793714400,v1=5d41402abc4b2a76b9719d911017c592…

{
  "id": "7f1c2a9e-4b1d-4e0a-9a51-2f7c8d1e6b30",
  "type": "meeting.ended",
  "created_at": "2026-11-03T15:00:02Z",
  "data": {
    "meeting": { "id": 14231, "status": "completed", "ended_reason": "scheduled_end", … }
  }
}`;

  const signatureExample = `const crypto = require("crypto");

// rawBody: the request body exactly as received, before JSON parsing
function verifyEmotiiSignature(rawBody, signatureHeader, secret) {
  const parts = Object.fromEntries(signatureHeader.split(",").map((p) => p.split("=")));
  const expected = crypto
    .createHmac("sha256", secret)
    .update(\`\${parts.t}.\${rawBody}\`)
    .digest("hex");

  const fresh = Math.abs(Date.now() / 1000 - Number(parts.t)) < 300;
  const sameLength = expected.length === (parts.v1 || "").length;
  return fresh && sameLength && crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(parts.v1));
}`;

  const validationResponse = `{
  "error": "Invalid meeting",
  "errors": [
    "start_at must be in the future",
    "organizers must be registered emotii accounts: rep@acme.com"
  ]
}`;

  const curlExample = `# Create a meeting
curl --location '${endpoint}' \\
  --header 'X-API-Key: YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --header 'Idempotency-Key: crm-opportunity-8812' \\
  --data '{
    "title": "Renewal call",
    "description": "Quarterly renewal",
    "start_at": "2026-11-03T14:00:00Z",
    "end_at": "2026-11-03T15:00:00Z",
    "organizers": ["rep@acme.com"],
    "invitees": ["client@example.com"],
    "ai_summary_enabled": true,
    "transcript_enabled": true,
    "external_reference": "crm-opportunity-8812"
  }'

# List meetings (optionally filtered)
curl --location '${endpoint}?page=1&per_page=25&external_reference=crm-opportunity-8812' \\
  --header 'X-API-Key: YOUR_API_KEY'

# Get one meeting
curl --location '${endpoint}/MEETING_ID' \\
  --header 'X-API-Key: YOUR_API_KEY'

# Reschedule and change invitees
curl --location '${endpoint}/MEETING_ID' \\
  --request PATCH \\
  --header 'X-API-Key: YOUR_API_KEY' \\
  --header 'Content-Type: application/json' \\
  --data '{
    "start_at": "2026-11-04T14:00:00Z",
    "end_at": "2026-11-04T15:00:00Z",
    "invitees": ["client@example.com", "cfo@example.com"]
  }'

# Cancel
curl --location '${endpoint}/MEETING_ID' \\
  --request DELETE \\
  --header 'X-API-Key: YOUR_API_KEY'

# After the meeting: AI summary and transcript
curl --location '${endpoint}/MEETING_ID/summary' \\
  --header 'X-API-Key: YOUR_API_KEY'

curl --location '${endpoint}/MEETING_ID/transcript' \\
  --header 'X-API-Key: YOUR_API_KEY'`;

  return (
    <Container>
      <HeaderRow>
        <Title data-emotii-exclude>API for Meetings</Title>
        <HeaderMeta>
          <Badge tone="brand" data-emotii-exclude>v1</Badge>
          <DividerDot />
          <Small>REST • JSON</Small>
        </HeaderMeta>
      </HeaderRow>

      <PageContent>
        <Grid>
          <Card $span2>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <Note>
              Create and manage emotii meetings from your own systems, such as a CRM, ERP or scheduling tool.
              Meetings created through the API are regular emotii meetings: invitees receive the usual invitation
              emails, organizers host from the emotii apps, and everyone gets real-time interpretation, with
              optional AI summaries and transcripts.
            </Note>
            <Note style={{ marginTop: "12px" }}>
              <b>Scope:</b> each request only sees meetings that were created through the API in the project your
              key belongs to. Meetings created in the emotii apps are not listed or editable here.
            </Note>
            <Note style={{ marginTop: "12px" }}>
              <b>Lifecycle:</b> a meeting is <Mono data-emotii-exclude>scheduled</Mono> until an organizer joins and
              starts it (<Mono data-emotii-exclude>ongoing</Mono>); invitees who arrive earlier wait for the host. It
              becomes <Mono data-emotii-exclude>completed</Mono> at{" "}
              <Mono data-emotii-exclude>end_at</Mono>, when it is closed automatically. A cancelled meeting has the
              status <Mono data-emotii-exclude>cancelled</Mono>. Only <Mono data-emotii-exclude>scheduled</Mono>{" "}
              meetings can be updated or cancelled.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Endpoints</CardTitle>
            </CardHeader>
            <Note>
              <b>Base URL:</b> <Mono data-emotii-exclude>{endpoint}</Mono>
              <br />
              <b>Authentication:</b> send your API for Meetings key in the{" "}
              <Mono data-emotii-exclude>X-API-Key</Mono> header. Keys created for other products are rejected.
            </Note>
            <WorkflowSteps>
              <Step>
                <StepNumber>1</StepNumber>
                <StepContent>
                  <StepTitle>Create a meeting</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>POST /meetings</Mono> - schedules the meeting and invites everyone</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>2</StepNumber>
                <StepContent>
                  <StepTitle>List meetings</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>GET /meetings</Mono> - paginated, newest start time first</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>3</StepNumber>
                <StepContent>
                  <StepTitle>Get a meeting</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>GET /meetings/:id</Mono> - current status and details</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>4</StepNumber>
                <StepContent>
                  <StepTitle>Update a meeting</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>PATCH /meetings/:id</Mono> - reschedule, rename or change participants</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>5</StepNumber>
                <StepContent>
                  <StepTitle>Cancel a meeting</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>DELETE /meetings/:id</Mono> - cancels and notifies participants</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>6</StepNumber>
                <StepContent>
                  <StepTitle>Get the AI summary</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>GET /meetings/:id/summary</Mono> - after the meeting, one summary per participant language</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>7</StepNumber>
                <StepContent>
                  <StepTitle>Get the transcript</StepTitle>
                  <StepDesc><Mono data-emotii-exclude>GET /meetings/:id/transcript</Mono> - after the meeting, with translations</StepDesc>
                </StepContent>
              </Step>
              <Step>
                <StepNumber>8</StepNumber>
                <StepContent>
                  <StepTitle>Receive webhooks</StepTitle>
                  <StepDesc>Signed events when meetings start, end, or their summary and transcript are ready</StepDesc>
                </StepContent>
              </Step>
            </WorkflowSteps>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Create a meeting</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>POST</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}</EndpointValue>
            </EndpointRow>
            <Note style={{ marginTop: "12px" }}>
              <b>Headers:</b> <Mono data-emotii-exclude>X-API-Key</Mono>,{" "}
              <Mono data-emotii-exclude>Content-Type: application/json</Mono>, and optionally{" "}
              <Mono data-emotii-exclude>Idempotency-Key</Mono>
            </Note>

            <SubTitle>Body</SubTitle>
            <List>
              <Item>
                <Key data-emotii-exclude>title</Key>
                <Desc><RequiredBadge>Required</RequiredBadge> Meeting title.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>start_at</Key>
                <Desc>
                  <RequiredBadge>Required</RequiredBadge> ISO 8601 timestamp with a timezone, e.g.{" "}
                  <Mono data-emotii-exclude>2026-11-03T14:00:00Z</Mono>. Must be in the future.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>end_at</Key>
                <Desc>
                  <RequiredBadge>Required</RequiredBadge> ISO 8601 timestamp with a timezone. Must be after{" "}
                  <Mono data-emotii-exclude>start_at</Mono>; a meeting can last at most 12 hours.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>organizers</Key>
                <Desc>
                  <RequiredBadge>Required</RequiredBadge> Array of emails, at least one. Each must be a registered
                  emotii account. The first email is the main organizer; the others become co-organizers.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>invitees</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge> Array of emails. Invitees don't need an emotii account and
                  can't also be listed as organizers.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>description</Key>
                <Desc><OptionalBadge>Optional</OptionalBadge> Shown in the invitation.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>ai_summary_enabled</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge> <Mono data-emotii-exclude>true</Mono> to email an AI
                  summary to the organizer after the meeting. Also available from Get the AI summary.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>transcript_enabled</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge> <Mono data-emotii-exclude>true</Mono> to email the
                  transcript to the organizer after the meeting. Also available from Get the transcript.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>external_reference</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge> Your own ID for this meeting, e.g. a CRM record. Returned
                  in every response and usable as a filter when listing.
                </Desc>
              </Item>
            </List>

            <Callout>
              <b>Avoid duplicates with Idempotency-Key.</b> If a request with the same{" "}
              <Mono data-emotii-exclude>Idempotency-Key</Mono> was already processed for your API key, the existing
              meeting is returned with <Mono data-emotii-exclude>200</Mono> instead of creating a new one. Safe to
              retry after timeouts.
            </Callout>

            <Note>
              If your plan limits the number of participants per meeting, organizers plus invitees must not exceed it.
            </Note>

            <SubTitle>
              Returns <StatusPill data-emotii-exclude>201</StatusPill>
            </SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{meetingResponse}</code>
              </CodeArea>
            </CodeBlock>
            <Note style={{ marginTop: "16px" }}>
              Use <Mono data-emotii-exclude>join_url</Mono>, or <Mono data-emotii-exclude>display_id</Mono> with{" "}
              <Mono data-emotii-exclude>passcode</Mono>, to join the meeting.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>List meetings</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>GET</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}</EndpointValue>
            </EndpointRow>

            <SubTitle>Query parameters</SubTitle>
            <List>
              <Item>
                <Key data-emotii-exclude>page</Key>
                <Desc><OptionalBadge>Optional</OptionalBadge> Page number. Defaults to <Mono data-emotii-exclude>1</Mono>.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>per_page</Key>
                <Desc>
                  <OptionalBadge>Optional</OptionalBadge> Defaults to <Mono data-emotii-exclude>25</Mono>, maximum{" "}
                  <Mono data-emotii-exclude>100</Mono>.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>external_reference</Key>
                <Desc><OptionalBadge>Optional</OptionalBadge> Only meetings with this reference.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>from</Key>
                <Desc><OptionalBadge>Optional</OptionalBadge> Only meetings starting at or after this ISO 8601 time.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>to</Key>
                <Desc><OptionalBadge>Optional</OptionalBadge> Only meetings starting at or before this ISO 8601 time.</Desc>
              </Item>
            </List>

            <SubTitle>
              Returns <StatusPill data-emotii-exclude>200</StatusPill>
            </SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{listResponse}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Get a meeting</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>GET</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:id</EndpointValue>
            </EndpointRow>
            <Note>
              Returns <StatusPill data-emotii-exclude>200</StatusPill> with the same <Mono data-emotii-exclude>meeting</Mono>{" "}
              object as Create. After the meeting, <Mono data-emotii-exclude>ended_reason</Mono> is{" "}
              <Mono data-emotii-exclude>scheduled_end</Mono> or <Mono data-emotii-exclude>quota_exhausted</Mono>.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Update a meeting</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>PATCH</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:id</EndpointValue>
            </EndpointRow>
            <Note>
              Send only the fields you want to change; they follow the same rules as Create. Sending{" "}
              <Mono data-emotii-exclude>organizers</Mono> or <Mono data-emotii-exclude>invitees</Mono> replaces that
              whole list. New participants receive an invitation, and existing participants are told about changes
              to the title, description or time.
            </Note>
            <Note>
              Returns <StatusPill data-emotii-exclude>200</StatusPill> with the updated meeting. Only{" "}
              <Mono data-emotii-exclude>scheduled</Mono> meetings can be updated.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Cancel a meeting</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>DELETE</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:id</EndpointValue>
            </EndpointRow>
            <Note>
              Sets the status to <Mono data-emotii-exclude>cancelled</Mono> and notifies all participants. The
              meeting is not deleted and is still returned by List and Get. Returns{" "}
              <StatusPill data-emotii-exclude>200</StatusPill> with the cancelled meeting. Only{" "}
              <Mono data-emotii-exclude>scheduled</Mono> meetings can be cancelled.
            </Note>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Get the AI summary</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>GET</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:id/summary</EndpointValue>
            </EndpointRow>
            <Note>
              Available once the meeting is <Mono data-emotii-exclude>completed</Mono>, for meetings created with{" "}
              <Mono data-emotii-exclude>ai_summary_enabled: true</Mono>. A summary is generated in each
              participant's language; long meetings are split into several parts.
            </Note>
            <SubTitle>Status</SubTitle>
            <List>
              <Item>
                <Key data-emotii-exclude>pending / processing</Key>
                <Desc>The summary is being generated. Try again in a few minutes.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>completed</Key>
                <Desc>All languages are ready.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>partially_completed</Key>
                <Desc>Some languages failed; the ones that succeeded are returned.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>failed</Key>
                <Desc>No summary could be generated.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>unavailable</Key>
                <Desc>The meeting never started, so there is nothing to summarize.</Desc>
              </Item>
            </List>
            <SubTitle>
              Returns <StatusPill data-emotii-exclude>200</StatusPill>
            </SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{summaryResponse}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Get the transcript</CardTitle>
            </CardHeader>
            <EndpointRow>
              <EndpointLabel>GET</EndpointLabel>
              <EndpointValue data-emotii-exclude>{endpoint}/:id/transcript</EndpointValue>
            </EndpointRow>
            <Note>
              Available once the meeting is <Mono data-emotii-exclude>completed</Mono>, for meetings created with{" "}
              <Mono data-emotii-exclude>transcript_enabled: true</Mono>. <Mono data-emotii-exclude>entries</Mono>{" "}
              lists every spoken line with its translations by language; <Mono data-emotii-exclude>text</Mono> is
              the same transcript as plain text.
            </Note>
            <SubTitle>
              Returns <StatusPill data-emotii-exclude>200</StatusPill>
            </SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{transcriptResponse}</code>
              </CodeArea>
            </CodeBlock>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Webhooks</CardTitle>
            </CardHeader>
            <Note>
              Add up to 5 endpoints in the <b>Webhooks</b> section of the API for Meetings page. Each endpoint
              receives the events you select, or all of them, and has its own signing secret.
            </Note>

            <SubTitle>Events</SubTitle>
            <List>
              <Item>
                <Key data-emotii-exclude>meeting.created</Key>
                <Desc>A meeting was created through the API.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>meeting.updated</Key>
                <Desc>A scheduled meeting was changed.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>meeting.cancelled</Key>
                <Desc>A scheduled meeting was cancelled.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>meeting.started</Key>
                <Desc>An organizer joined and started the meeting.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>meeting.ended</Key>
                <Desc>
                  The meeting was closed; <Mono data-emotii-exclude>ended_reason</Mono> is{" "}
                  <Mono data-emotii-exclude>scheduled_end</Mono> or <Mono data-emotii-exclude>quota_exhausted</Mono>.
                </Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>meeting.summary_ready</Key>
                <Desc>The AI summary can be fetched with Get the AI summary.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>meeting.transcript_ready</Key>
                <Desc>The transcript can be fetched with Get the transcript.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>webhook.test</Key>
                <Desc>Sent when you click Test for an endpoint. <Mono data-emotii-exclude>data</Mono> is empty.</Desc>
              </Item>
            </List>

            <SubTitle>Request</SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{webhookPayload}</code>
              </CodeArea>
            </CodeBlock>
            <Note style={{ marginTop: "12px" }}>
              <Mono data-emotii-exclude>data.meeting</Mono> is the same object the API returns. Respond with any{" "}
              <Mono data-emotii-exclude>2xx</Mono> status within 10 seconds.
            </Note>

            <SubTitle>Verifying the signature</SubTitle>
            <Note>
              <Mono data-emotii-exclude>Emotii-Signature</Mono> contains a timestamp{" "}
              <Mono data-emotii-exclude>t</Mono> and <Mono data-emotii-exclude>v1</Mono>, the HMAC-SHA256 of{" "}
              <Mono data-emotii-exclude>t.body</Mono> signed with the endpoint's secret. Reject requests whose
              signature doesn't match or whose timestamp is older than a few minutes.
            </Note>
            <CodeBlock>
              <Toolbar>
                <LangPill data-emotii-exclude>javascript</LangPill>
                <Spacer />
                <CopyBtn
                  onClick={() => handleCopy("signature", signatureExample)}
                  data-copied={copied === "signature" ? "true" : "false"}
                >
                  <Copy />
                  {copied === "signature" ? "Copied" : "Copy"}
                </CopyBtn>
              </Toolbar>
              <CodeArea data-emotii-exclude>
                <code>{signatureExample}</code>
              </CodeArea>
            </CodeBlock>

            <Callout>
              <b>Delivery is at least once.</b> A failed delivery is retried after 1 minute, 5 minutes, 30 minutes,
              2 hours, 6 hours and 12 hours, so the same event can arrive more than once; use{" "}
              <Mono data-emotii-exclude>Emotii-Event-Id</Mono> to ignore duplicates. After 10 failed deliveries in a
              row the endpoint is disabled and project admins are emailed. Failed deliveries can be resent from the
              delivery log.
            </Callout>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Limits and usage</CardTitle>
            </CardHeader>
            <List>
              <Item>
                <Key data-emotii-exclude>Requests</Key>
                <Desc>64 requests per minute per API key.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>New meetings</Key>
                <Desc>64 meetings created per hour per project.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>Duration</Key>
                <Desc>Up to 12 hours per meeting.</Desc>
              </Item>
              <Item>
                <Key data-emotii-exclude>Usage</Key>
                <Desc>
                  Words, audio minutes and video minutes used in API meetings count towards your API for Meetings
                  plan. If the plan runs out during a meeting, the meeting is ended with{" "}
                  <Mono data-emotii-exclude>ended_reason: "quota_exhausted"</Mono>, and participants can't join API
                  meetings until the plan has usage available again.
                </Desc>
              </Item>
            </List>
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Complete Example</CardTitle>
              <Tabs>
                <Tab aria-selected={activeTab === "flow"} onClick={() => setActiveTab("flow")}>
                  Flow Diagram
                </Tab>
                <Tab aria-selected={activeTab === "curl"} onClick={() => setActiveTab("curl")}>
                  cURL
                </Tab>
              </Tabs>
            </CardHeader>

            {activeTab === "flow" && (
              <FlowDiagram>
                <FlowStep>
                  <FlowLabel>Your system</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>POST /meetings</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Meeting scheduled, invitations sent</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Organizer</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Joins via join_url and starts it</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Status: ongoing, invitees can join</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>emotii</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Closes the meeting at end_at</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Status: completed, summary and transcript emailed</FlowLabel>
                </FlowStep>
                <FlowStep>
                  <FlowLabel>Your system</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>GET /meetings/:id/summary, /transcript</FlowLabel>
                  <FlowArrow>→</FlowArrow>
                  <FlowLabel>Store the summary and transcript</FlowLabel>
                </FlowStep>
              </FlowDiagram>
            )}

            {activeTab === "curl" && (
              <CodeBlock>
                <Toolbar>
                  <LangPill data-emotii-exclude>bash</LangPill>
                  <Spacer />
                  <CopyBtn
                    onClick={() => handleCopy("curl", curlExample)}
                    data-copied={copied === "curl" ? "true" : "false"}
                  >
                    <Copy />
                    {copied === "curl" ? "Copied" : "Copy"}
                  </CopyBtn>
                </Toolbar>
                <CodeArea role="region" aria-label="curl Example" data-emotii-exclude>
                  <code>{curlExample}</code>
                </CodeArea>
              </CodeBlock>
            )}
          </Card>

          <Card $span2>
            <CardHeader>
              <CardTitle>Error Handling</CardTitle>
            </CardHeader>
            <ErrorList>
              <ErrorRow>
                <ErrCode data-emotii-exclude>401</ErrCode>
                <ErrText>Missing or invalid <Mono data-emotii-exclude>X-API-Key</Mono>.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>402</ErrCode>
                <ErrText>
                  No active API for Meetings subscription, or the plan's usage limit is reached. The response
                  includes a <Mono data-emotii-exclude>quota</Mono> object.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>403</ErrCode>
                <ErrText>The API key belongs to a different product.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>404</ErrCode>
                <ErrText>
                  The meeting doesn't exist or wasn't created through the API in your project, or the summary or
                  transcript wasn't enabled for it.
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>409</ErrCode>
                <ErrText>
                  The meeting is ongoing, completed or cancelled and can no longer be changed (
                  <Mono data-emotii-exclude>error_code: "ACCESS1014"</Mono>), or the summary or transcript was
                  requested before the meeting ended or for a cancelled meeting (
                  <Mono data-emotii-exclude>error_code: "ACCESS1017"</Mono>).
                </ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>422</ErrCode>
                <ErrText>Validation failed. The <Mono data-emotii-exclude>errors</Mono> array lists every problem.</ErrText>
              </ErrorRow>
              <ErrorRow>
                <ErrCode data-emotii-exclude>429</ErrCode>
                <ErrText>
                  Rate limit reached (<Mono data-emotii-exclude>error_code: "ACCESS1016"</Mono>). Retry after the
                  number of seconds in the <Mono data-emotii-exclude>Retry-After</Mono> header.
                </ErrText>
              </ErrorRow>
            </ErrorList>

            <SubTitle>Example 422 response</SubTitle>
            <CodeBlock>
              <CodeArea data-emotii-exclude>
                <code>{validationResponse}</code>
              </CodeArea>
            </CodeBlock>
          </Card>
        </Grid>
      </PageContent>
    </Container>
  );
};

export default MeetingsApiDocumentation;
